import React, { useEffect, useMemo, useState } from "react"
import {
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Form,
  message,
  Row,
  Select,
  Space,
  Tag,
  Typography,
  Popconfirm,
} from "antd"
import {
  SearchOutlined,
  SaveOutlined,
  UserOutlined,
  CalendarOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  ReloadOutlined,
} from "@ant-design/icons"
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  setDoc,
  Timestamp,
  serverTimestamp,
} from "firebase/firestore"
import { db } from "../../../firebase/config"
import { useAuthContext } from "../../../hooks/useAuthContext"

const { Title, Text } = Typography
const { Option } = Select

// ---------- helpers ----------
function round2(num) {
  return Math.round((Number(num || 0) + Number.EPSILON) * 100) / 100
}
function pad2(n) {
  return String(n).padStart(2, "0")
}
function monthBoundariesLocal(year, month1to12) {
  const start = new Date(year, month1to12 - 1, 1, 0, 0, 0, 0)
  const end = new Date(year, month1to12, 1, 0, 0, 0, 0) // first of next month
  const asOf = new Date(end.getTime() - 1)
  return { start, end, asOf }
}
function nowYearMonth() {
  const d = new Date()
  return { y: d.getFullYear(), m: d.getMonth() + 1 }
}
function currency(n) {
  return `€${Number(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`
}
const MONTHS = [
  { key: 1, label: "January" },
  { key: 2, label: "February" },
  { key: 3, label: "March" },
  { key: 4, label: "April" },
  { key: 5, label: "May" },
  { key: 6, label: "June" },
  { key: 7, label: "July" },
  { key: 8, label: "August" },
  { key: 9, label: "September" },
  { key: 10, label: "October" },
  { key: 11, label: "November" },
  { key: 12, label: "December" },
]
function monthLabel(m) {
  return MONTHS.find((x) => x.key === m)?.label ?? String(m)
}
function hasChanges(current, next) {
  if (!current) return true
  const keys = [
    "totalNetoSumMonth",
    "profit",
    "missingPaymentBase",
    "missingPaymentDeduction",
    "bankruptcyRiskDeduction",
    "stuck30Base",
    "stuck60Base",
    "stuck30Deduction",
    "stuck60Deduction",
    "calculatedEarnings",
  ]
  return keys.some((k) => Math.abs(Number(next[k] ?? 0) - Number(current[k] ?? 0)) > 0.009)
}

export default function SalaryReCalculator() {
  const { user } = useAuthContext()

  // selections
  const now = useMemo(nowYearMonth, [])
  const [salesUsers, setSalesUsers] = useState([])
  const [loadingUsers, setLoadingUsers] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState(null)
  const [selectedYear, setSelectedYear] = useState(now.y)
  const [selectedMonth, setSelectedMonth] = useState(now.m)

  // preview / state
  const [preview, setPreview] = useState(null) // {current, next, monthKey, counts, reportId}
  const [busy, setBusy] = useState(false)

  const years = useMemo(() => {
    const cur = new Date().getFullYear()
    const arr = []
    for (let y = 2018; y <= cur; y++) arr.push(y)
    return arr
  }, [])

  // ---------- load sales users ----------
  useEffect(() => {
    async function loadUsers() {
      setLoadingUsers(true)
      try {
        const qUsers = query(collection(db, "users"), where("group", "array-contains", "sales"))
        const snap = await getDocs(qUsers)
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
        setSalesUsers(list)
        const me = list.find((u) => u.id === user?.uid)
        setSelectedUserId(me ? me.id : list[0]?.id || null)
      } catch (e) {
        console.error(e)
        message.error("Failed to load sales users")
      } finally {
        setLoadingUsers(false)
      }
    }
    loadUsers()
  }, [user?.uid])

  // ---------- recompute (client-side) ----------
  async function recomputeFor(userId, year, month1to12) {
    const { start, end, asOf } = monthBoundariesLocal(year, month1to12)
    const startTs = Timestamp.fromDate(start)
    const endTs = Timestamp.fromDate(end)

    // 1) Month net (documentDate range)
    const qMonth = query(
      collection(db, "invoices"),
      where("userId", "==", userId),
      where("documentDate", ">=", startTs),
      where("documentDate", "<", endTs)
    )
    const monthSnap = await getDocs(qMonth)
    let totalNetThisMonth = 0
    let invoicesCount = 0
    monthSnap.forEach((d) => {
      const inv = d.data()
      const net = Number(inv?.totalData?.totalNetoSum || 0)
      totalNetThisMonth += net
      invoicesCount++
    })
    totalNetThisMonth = round2(totalNetThisMonth)

    // 2) Overdue base (unpaid, payUntil <= asOf - 30d)
    const cutoffDate = new Date(asOf)
    cutoffDate.setDate(cutoffDate.getDate() - 30)
    const cutoffTs = Timestamp.fromDate(cutoffDate)

    const qOverdue = query(
      collection(db, "invoices"),
      where("userId", "==", userId),
      where("paymentStatus", "==", 0),
      where("payUntil", "<=", cutoffTs)
    )
    const overdueSnap = await getDocs(qOverdue)
    let overdueBase = 0
    let overdueOrderIds = []
    let missingPaymentInvoiceIds = []
    let bankruptcyBase = 0
    let overdueCount = 0

    overdueSnap.forEach((d) => {
      const inv = d.data()
      const net = Number(inv?.totalData?.totalNetoSum || 0)
      overdueBase += net
      overdueCount++
      if (Array.isArray(inv?.orderReferenceId) && inv.orderReferenceId.length) {
        overdueOrderIds.push(...inv.orderReferenceId)
      }
      missingPaymentInvoiceIds.push(d.id)
      if (inv?.clientBankrupcy === true) {
        bankruptcyBase += net
      }
    })

    overdueBase = round2(overdueBase)
    bankruptcyBase = round2(bankruptcyBase)

    // 3) Stuck orders (status 6) with 30/60 thresholds as of end-of-month
    const threshold30 = new Date(asOf)
    threshold30.setDate(threshold30.getDate() - 30)
    const threshold60 = new Date(asOf)
    threshold60.setDate(threshold60.getDate() - 60)

    const qStuck = query(
      collection(db, "orders"),
      where("uid", "==", userId),
      where("orderStatus", "==", 6)
    )
    const stuckSnap = await getDocs(qStuck)

    let base30 = 0
    let base60 = 0
    let ids30 = []
    let ids60 = []
    let stuck30Count = 0
    let stuck60Count = 0

    stuckSnap.forEach((d) => {
      const o = d.data()
      const value = Number(o?.totalValue || 0)
      const ts = o?.createdTimestamp ?? o?.created
      let createdDate = null
      if (ts && typeof ts.toDate === "function") createdDate = ts.toDate()
      else if (ts) createdDate = new Date(ts)
      if (!createdDate || isNaN(createdDate.getTime())) return

      if (createdDate <= threshold30) {
        if (createdDate <= threshold60) {
          base60 += value
          ids60.push(d.id)
          stuck60Count++
        } else {
          base30 += value
          ids30.push(d.id)
          stuck30Count++
        }
      }
    })

    base30 = round2(base30)
    base60 = round2(base60)

    // 4) Deductions & earnings
    const profitThisMonth = round2(totalNetThisMonth * 0.05)
    const missingPaymentDeduction = round2(overdueBase * 0.05)
    const bankruptcyDeduction = round2(bankruptcyBase * 0.1)
    const stuck30Deduction = round2(base30 * 0.05)
    const stuck60Deduction = round2(base60 * 0.1)
    const calculatedEarnings = round2(
      profitThisMonth -
        (missingPaymentDeduction + bankruptcyDeduction + stuck30Deduction + stuck60Deduction)
    )

    const monthKey = `${year}_${pad2(month1to12)}`
    const payload = {
      userId,
      totalNetoSumMonth: totalNetThisMonth,
      profit: profitThisMonth,
      missingPaymentBase: overdueBase,
      missingPaymentDeduction,
      bankruptcyRiskDeduction: bankruptcyDeduction,
      missingOrderIds: overdueOrderIds,
      missingPaymentInvoiceIds,
      stuck30Base: base30,
      stuck60Base: base60,
      stuck30OrderIds: ids30,
      stuck60OrderIds: ids60,
      stuck30Deduction,
      stuck60Deduction,
      calculatedEarnings,
      lastUpdated: serverTimestamp(),
      _recomputedFromClient: true,
      _recomputedAsOf: Timestamp.fromDate(asOf),
      // store counts for auditing
      _invoicesCount: invoicesCount,
      _overdueCount: overdueCount,
      _stuck30Count: stuck30Count,
      _stuck60Count: stuck60Count,
    }

    const counts = {
      invoicesCount,
      overdueCount,
      stuck30Count,
      stuck60Count,
    }

    return { payload, counts, monthKey }
  }

  async function handlePreview() {
    if (!selectedUserId) return message.error("Pick a user")
    setBusy(true)
    const hide = message.loading("Building preview…", 0)
    try {
      const { payload, counts, monthKey } = await recomputeFor(
        selectedUserId,
        selectedYear,
        selectedMonth
      )
      const reportId = `${selectedUserId}_${monthKey}`
      const snap = await getDoc(doc(db, "salesReports", reportId))
      const current = snap.exists() ? snap.data() : null
      setPreview({ current, next: payload, counts, monthKey, reportId })
      hide()
      message.success("Preview ready")
    } catch (e) {
      console.error(e)
      hide()
      message.error("Failed to build preview")
    } finally {
      setBusy(false)
    }
  }

  async function handleApply() {
    if (!preview?.next || !preview?.reportId) return
    setBusy(true)
    const hide = message.loading("Saving changes…", 0)
    try {
      await setDoc(doc(db, "salesReports", preview.reportId), preview.next, { merge: true })
      hide()
      message.success(`Updated ${preview.reportId}`)
    } catch (e) {
      console.error(e)
      hide()
      message.error("Failed to save changes")
    } finally {
      setBusy(false)
    }
  }

  const numberRow = (label, oldVal, newVal) => {
    const o = Number(oldVal ?? 0)
    const n = Number(newVal ?? 0)
    const delta = round2(n - o)
    const changed = Math.abs(delta) > 0.009
    const up = delta > 0
    const color = !changed ? undefined : up ? "#52c41a" : "#ff4d4f"
    return (
      <Descriptions.Item label={label}>
        <Space size="small" wrap>
          <Text type={changed ? "secondary" : undefined} delete={changed}>
            {currency(o)}
          </Text>
          {changed && (
            <>
              <Text strong>{`→ ${currency(n)}`}</Text>
              <Tag color={up ? "success" : "error"} style={{ borderRadius: 999 }}>
                {up ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {currency(Math.abs(delta))}
              </Tag>
            </>
          )}
        </Space>
      </Descriptions.Item>
    )
  }

  const countRow = (label, oldVal, newVal) => {
    const o = Number(oldVal ?? 0)
    const n = Number(newVal ?? 0)
    const delta = n - o
    const changed = delta !== 0
    const up = delta > 0
    return (
      <Descriptions.Item label={label}>
        <Space size="small" wrap>
          <Text type={changed ? "secondary" : undefined} delete={changed}>
            {o}
          </Text>
          {changed && (
            <>
              <Text strong>→ {n}</Text>
              <Tag color={up ? "success" : "error"} style={{ borderRadius: 999 }}>
                {up ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {Math.abs(delta)}
              </Tag>
            </>
          )}
        </Space>
      </Descriptions.Item>
    )
  }

  const selectedUserLabel =
    salesUsers.find((u) => u.id === selectedUserId)?.displayName ||
    salesUsers.find((u) => u.id === selectedUserId)?.email ||
    selectedUserId ||
    "—"

  const canApply = preview && hasChanges(preview.current, preview.next)

  return (
    <Card>
      <Row align="middle" justify="space-between">
        <Col>
          <Space size="small" direction="vertical">
            <Title level={4} style={{ margin: 0 }}>
              Manual Salary Report Recalculator
            </Title>
            <Space split={<Divider type="vertical" style={{ margin: 0 }} />}>
              <Text type="secondary">
                <UserOutlined /> {selectedUserLabel}
              </Text>
              <Text type="secondary">
                <CalendarOutlined /> {monthLabel(selectedMonth)} {selectedYear}
              </Text>
              {preview && (
                <Tag color="blue" style={{ borderRadius: 999 }}>
                  {preview.reportId}
                </Tag>
              )}
            </Space>
          </Space>
        </Col>
        <Col>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => setPreview(null)}>
              Reset
            </Button>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handlePreview}
              loading={busy}
              disabled={!selectedUserId}>
              Preview recalculation
            </Button>
          </Space>
        </Col>
      </Row>

      <Divider />

      <Form layout="inline" onFinish={handlePreview}>
        <Form.Item label="User" style={{ minWidth: 300 }}>
          <Select
            showSearch
            loading={loadingUsers}
            value={selectedUserId}
            onChange={setSelectedUserId}
            placeholder="Select sales user"
            optionFilterProp="children"
            style={{ minWidth: 300 }}>
            {salesUsers.map((u) => (
              <Option key={u.id} value={u.id}>
                {u.displayName || u.email || u.id}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Year">
          <Select value={selectedYear} onChange={setSelectedYear} style={{ width: 120 }}>
            {years.map((y) => (
              <Option key={y} value={y}>
                {y}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Month">
          <Select value={selectedMonth} onChange={setSelectedMonth} style={{ width: 160 }}>
            {MONTHS.map((m) => (
              <Option key={m.key} value={m.key}>
                {m.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={busy} disabled={!selectedUserId}>
            Preview recalculation
          </Button>
        </Form.Item>
      </Form>

      {preview && (
        <>
          <Divider />
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Card size="small" title="Money">
                <Descriptions column={1} size="small" colon>
                  {numberRow(
                    "Total net of month",
                    preview.current?.totalNetoSumMonth,
                    preview.next.totalNetoSumMonth
                  )}
                  {numberRow("Profit (5%)", preview.current?.profit, preview.next.profit)}
                  {numberRow(
                    "Missing payment base",
                    preview.current?.missingPaymentBase,
                    preview.next.missingPaymentBase
                  )}
                  {numberRow(
                    "Missing payment deduction (5%)",
                    preview.current?.missingPaymentDeduction,
                    preview.next.missingPaymentDeduction
                  )}
                  {numberRow(
                    "Bankruptcy deduction (10%)",
                    preview.current?.bankruptcyRiskDeduction,
                    preview.next.bankruptcyRiskDeduction
                  )}
                  {numberRow(
                    "Stuck 30 base",
                    preview.current?.stuck30Base,
                    preview.next.stuck30Base
                  )}
                  {numberRow(
                    "Stuck 30 deduction (5%)",
                    preview.current?.stuck30Deduction,
                    preview.next.stuck30Deduction
                  )}
                  {numberRow(
                    "Stuck 60 base",
                    preview.current?.stuck60Base,
                    preview.next.stuck60Base
                  )}
                  {numberRow(
                    "Stuck 60 deduction (10%)",
                    preview.current?.stuck60Deduction,
                    preview.next.stuck60Deduction
                  )}
                  {numberRow(
                    "Calculated earnings",
                    preview.current?.calculatedEarnings,
                    preview.next.calculatedEarnings
                  )}
                </Descriptions>
              </Card>
            </Col>

            <Col xs={24} lg={12}>
              <Card size="small" title="Counts">
                <Descriptions column={1} size="small" colon>
                  {countRow(
                    "Invoices counted in month",
                    preview.current?._invoicesCount,
                    preview.counts.invoicesCount
                  )}
                  {countRow(
                    "Overdue invoices (30+)",
                    preview.current?._overdueCount,
                    preview.counts.overdueCount
                  )}
                  {countRow(
                    "Stuck 30 orders",
                    preview.current?._stuck30Count,
                    preview.counts.stuck30Count
                  )}
                  {countRow(
                    "Stuck 60 orders",
                    preview.current?._stuck60Count,
                    preview.counts.stuck60Count
                  )}
                </Descriptions>
                <Text type="secondary">
                  Historical reports may not have stored counts; the right side shows fresh counts.
                </Text>
              </Card>
            </Col>
          </Row>

          <Space style={{ marginTop: 16 }}>
            <Popconfirm
              title="Apply recalculated values?"
              description="This will overwrite the saved monthly report for this user and month."
              okText="Apply"
              cancelText="Cancel"
              onConfirm={handleApply}
              disabled={!canApply}>
              <Button type="primary" icon={<SaveOutlined />} loading={busy} disabled={!canApply}>
                Apply changes
              </Button>
            </Popconfirm>
            {!canApply && <Text type="secondary">No changes detected — nothing to apply.</Text>}
          </Space>
        </>
      )}
    </Card>
  )
}
